// Import React modules
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
// Import other modules
import { ref, uploadBytesResumable, getStorage } from "firebase/storage";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Player } from "@lottiefiles/react-lottie-player";
import WarningIcon from "@mui/icons-material/Warning";
import Alert from "@mui/joy/Alert";
import Button from "@mui/joy/Button";
// Import Firebase config
import { auth, db, storage } from "../firebase";
// import { auth, db, app } from "../firebase";
// Import redux components
import { RootState } from "./../redux/store";
// Import CSS stylesheets
import "../styles/variables.css";
import "../styles/layoutStyles.css";
import "../styles/buttonStyles.css";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import UAParser from "ua-parser-js";
import axios from "axios";

interface AudioRecorderProps {
  mode: "generate" | "dictate";
  onAudioUrl: (url: string, fullFilename: string, isSetCurrentRecording: boolean) => void;
  onRecordingClick: () => void;
  sessionNotesUpload: string;
  noteTemplate: string;
  templateID: string;
  templateCategory: string;
  templateIsPreset: boolean;
}

function AudioRecorder({
  mode,
  onAudioUrl,
  onRecordingClick,
  sessionNotesUpload,
  noteTemplate,
  templateID,
  templateCategory,
  templateIsPreset,
}: // noteCategory,
// isPreset,
AudioRecorderProps) {
  const user = useSelector((state: RootState) => state.user);
  const [recording, setRecording] = useState<boolean>(false);
  const [paused, setPaused] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const [timer, setTimer] = useState<number>(0);
  const [timerDisplay, setTimerDisplay] = useState<string>("00:00:00");
  const [recordingAnimationStopped, setRecordingAnimationStopped] =
    useState<boolean>(false);
  // const [showLongRecordingAlert, setShowLongRecordingAlert] =
  //   useState<boolean>(false);
  // const [showSecondLongRecordingAlert, setShowSecondLongRecordingAlert] =
  //   useState<boolean>(false);
  const playerRef = useRef<Player>(null);
  const audioChunks = useRef<BlobPart[]>([]);
  const url = useRef<string | null>(null);
  const recordingCounter = useRef(0);
  const [isUploading, setIsUploading] = useState(false);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const startTime = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const currentStartTime = Date.now();
    startTime.current = currentStartTime; // Use a ref to store start time

    if (recording && !paused) {
      intervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime.current; // Use ref here
        setTimer(Math.floor(elapsedTime / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [recording, paused]);

  // useEffect(() => {
  //   if (timer == 3600) {
  //     setShowLongRecordingAlert(true);
  //   }
  //   if (timer == 7200) {
  //     setShowSecondLongRecordingAlert(true);
  //   }
  // }, [timer]);

  useEffect(() => {
    const hours = Math.floor(timer / 3600);
    const minutes = Math.floor((timer % 3600) / 60);
    const seconds = timer % 60;
    setTimerDisplay(
      `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    );
  }, [timer]);

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Microphone permission granted, you can proceed with recording
      return stream;
    } catch (error) {
      // Microphone permission denied or an error occurred
      console.error("Microphone permission denied:", error);
      throw error;
    }
  };

  const startRecording = async () => {
    try {
      // console.trace("Start recording");
      if (
        mediaRecorder.current &&
        mediaRecorder.current.state === "recording"
      ) {
        return; // Prevent starting a new recording if already recording
      }
      recordingCounter.current = 0;
      const stream = await requestMicrophonePermission();

      // console.log("Starting recording..");
      // Detect the browser using the user agent
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      );
      const isChrome =
        /chrome/i.test(navigator.userAgent) &&
        navigator.userAgent.includes("Chrome");

      // Set MIME type based on browser
      const mimeType = isSafari
        ? "audio/mp4"
        : isChrome
        ? "audio/webm"
        : "audio/mp3";
      const options = {
        mimeType: mimeType,
      };
      mediaRecorder.current = new MediaRecorder(stream, options);
      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.start();
      setRecording(true);
    } catch (err) {
      console.error("Microphone permission denied:", err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      mediaRecorder.current.pause();
      if (playerRef.current) {
        playerRef.current.pause();
      }
    }
    setPaused(true);
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && mediaRecorder.current.state === "paused") {
      mediaRecorder.current.resume();
      if (playerRef.current) {
        playerRef.current.play();
      }
    }
    setPaused(false);
  };

  const stopRecording = async () => {
    // console.trace("Stop recording");
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    // setShowLongRecordingAlert(false);
    // setShowSecondLongRecordingAlert(false);
    if (mediaRecorder.current) {
      const stream = await mediaRecorder.current.stream;
      stream.getTracks().forEach((track) => track.stop());
      mediaRecorder.current.stop();
      if (playerRef.current) {
        playerRef.current.stop();
      }
      mediaRecorder.current.onstop = () => {
        recordingCounter.current += 1;
        // Need to execute Media Recorder onStop logic only on the second recording when using Safari
        if (!isSafari || (isSafari && recordingCounter.current === 2)) {
          // console.trace("Media recorder on stop");
          const audioBlob = new Blob(audioChunks.current, {
            type: "audio/wav",
          });
          url.current = URL.createObjectURL(audioBlob);
          // Prepare metadata
          const filename = `${user.uid}_${new Date().toISOString()}`;
          // console.log("Filename:", filename);
          const fileExtension = determineFileExtension();
          const fullFilename = `${filename}${fileExtension}`;
          onAudioUrl(url.current, fullFilename, true);
          uploadAudio(url.current, timer);
          audioChunks.current = [];
        }
      };
    }
    setRecording(false);
    setTimer(0);
  };

  function getOperatingSystem(result: UAParser.IResult): string {
    return `${result.os.name} ${result.os.version}`;
  }

  function getDevice(result: UAParser.IResult): string {
    // Device type might not be defined for all user agents, e.g., desktop browsers
    const deviceType = result.device.type ? result.device.type : "Desktop";
    const deviceModel = result.device.model ? result.device.model : "N/A";
    return `${deviceType} - ${deviceModel}`;
  }

  function getBrowser(result: UAParser.IResult): string {
    return `${result.browser.name} ${result.browser.version}`;
  }

  async function getIpAddress(): Promise<string> {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch (error) {
      console.error("Error fetching IP address:", error);
      return "N/A";
    }
  }

  function formatDuration(duration: number): string {
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;
    return [
      hours.toString().padStart(2, "0"),
      minutes.toString().padStart(2, "0"),
      seconds.toString().padStart(2, "0"),
    ].join(":");
  }

  const uploadAudio = async (url: string, duration: number) => {
    if (isUploading) {
      return; // Prevent multiple uploads at the same time
    }
    // console.log("Preparing to upload...");
    // console.log("Session notes:", sessionNotesUpload);
    setIsUploading(true);

    const parser = new UAParser();
    const result = parser.getResult();
    const operatingSystem = getOperatingSystem(result);
    const device = getDevice(result);
    const browser = getBrowser(result);
    const ipAddress = await getIpAddress();

    const response = await fetch(url);
    const blob = await response.blob();
    const formattedDuration = formatDuration(duration);

    const userDocRef = doc(db, "users", user.uid);
    const recordingsCollectionRef = collection(userDocRef, "recordings");
    const recordingData = {
      timestamp: serverTimestamp(),
      progress: "Preparing recording",
      sessionNotes: sessionNotesUpload,
      templateID: templateID,
      templateCategory: templateCategory,
      templateIsPreset: templateIsPreset,
      operatingSystem: operatingSystem,
      device: device,
      browser: browser,
      ipAddress: ipAddress,
      mode: mode,
      duration: formattedDuration,
    };

    try {
      // console.log("Attempting audio upload to cloud...");
      // Add the recording data to the "recordings" collection
      addDoc(recordingsCollectionRef, recordingData)
        .then((docRef) => {
          const recordingId = docRef.id;
          // console.log("Recording ID generated: ", recordingId);
          const recordingDocRef = doc(recordingsCollectionRef, recordingId);
          // Create audio file name
          const filename = `${
            user.uid
          }_${new Date().toISOString()}_${recordingId}`;
          // console.log("Filename:", filename);
          const fileExtension = determineFileExtension();
          const fullFilename = `${filename}${fileExtension}`;
          // const storage = getStorage(app, "gs://motics-consultations-staging");
          let storageRef;
          // const storageRef = ref(storage, `audioFiles/${fullFilename}`); NO LONGER USED
          if (mode === "dictate") {
            storageRef = ref(storage, `dictateAudioFiles/${fullFilename}`);
          } else {
            storageRef = ref(storage, `audioFiles/${fullFilename}`);
          }
          setUploadStatus("Metadata updated, starting file upload...");

          // Upload the audio file to Firebase Storage
          // console.trace("Uploading to storage");
          const uploadTask = uploadBytesResumable(storageRef, blob);

          uploadTask.on(
            "state_changed",
            (snapshot) => {},
            (error) => {
              console.error("Upload failed", error);
              setUploadStatus("Upload failed");
              const progressData = {
                progress: "Upload failed",
              };
              updateDoc(recordingDocRef, progressData)
                .then(() => {
                  // console.log("Progress field updated successfully");
                })
                .catch((error) => {
                  console.error("Error updating progress field: ", error);
                });
            },
            () => {
              // console.log("Uploaded to Firebase Cloud Storage");
              setUploadStatus(
                "Upload successful! Your summary will be emailed to you shortly."
              );
              const progressData = {
                progress: "Processing recording",
              };
              updateDoc(recordingDocRef, progressData)
                .then(() => {
                  // console.log("Progress field updated successfully");
                })
                .catch((error) => {
                  console.error("Error updating progress field: ", error);
                });
              setTimeout(() => {
                setUploadStatus("");
              }, 1500);
            }
          );
        })
        .catch((error) => {
          console.error("Error adding recording: ", error);
        });
    } catch (error) {
      console.error("Error updating Firestore or uploading file:", error);
    }
    setIsUploading(false);
  };

  function determineFileExtension() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isChrome =
      /chrome/i.test(navigator.userAgent) &&
      /google inc/i.test(navigator.vendor);
    return isSafari ? ".m4a" : isChrome ? ".webm" : ".mp3";
  }

  const handleRecordingClick = () => {
    // Call the general click handler
    onRecordingClick();

    // Then call either startRecording or stopRecording based on the recording state
    if (recording) {
      stopRecording();
    } else {
      if (noteTemplate === "") {
        alert("Please select a note template before starting recording.");
      } else {
        startRecording();
      }
    }
  };

  return (
    <div className="box-sizing-default audio-recorder-container">
      {/* {showLongRecordingAlert && (
        <Alert
          startDecorator={<WarningIcon />}
          variant="outlined"
          color="danger"
          margin-bottom="1rem"
          endDecorator={
            <React.Fragment>
              <Button
                variant="plain"
                color="danger"
                sx={{ mr: 1 }}
                onClick={stopRecording}
              >
                Stop
              </Button>
              <Button
                variant="plain"
                color="danger"
                sx={{ mr: 1 }}
                onClick={() => setShowLongRecordingAlert(false)}
              >
                Continue
              </Button>
            </React.Fragment>
          }
        >
          Recording has been going on for more than 1 hour. Do you want to
          continue recording?
        </Alert>
      )}
      {showSecondLongRecordingAlert && (
        <Alert
          startDecorator={<WarningIcon />}
          variant="outlined"
          color="danger"
          margin-bottom="1rem"
          endDecorator={
            <React.Fragment>
              <Button
                variant="plain"
                color="danger"
                sx={{ mr: 1 }}
                onClick={() => setShowSecondLongRecordingAlert(false)}
              >
                Okay
              </Button>
            </React.Fragment>
          }
        >
          Recording has been going on for more than 2 hours. Please stop the
          recording when the consultation is over and start a new recording for
          a new consultation.
        </Alert>
      )} */}
      <div style={{ display: "flex" }}>
        <button
          className={recording ? "button-record-red" : "button-record"}
          onClick={handleRecordingClick}
        >
          {recording ? "Stop Recording" : "Start Recording"}
        </button>
        {recording && (
          <button
            className={paused ? "button-classic-green" : "button-record"}
            onClick={paused ? resumeRecording : pauseRecording}
          >
            {paused ? "Resume Recording" : "Pause Recording"}
          </button>
        )}
      </div>
      {recording && (
        <>
          <div
            className="alert-text"
            style={{ textAlign: "center", marginTop: "0.5rem" }}
          >
            Please keep this tab open while recording.
          </div>
          <p>{timerDisplay}</p>
        </>
      )}
      {/* </div> */}
    </div>
  );
}
export default AudioRecorder;
